import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";

// Context for managing tooltip state
const TooltipContext = createContext<{
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}>({
  isOpen: false,
  setIsOpen: () => {},
});

// Provider component to manage tooltip state
export const TooltipProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <TooltipContext.Provider value={{ isOpen, setIsOpen }}>
      {children}
    </TooltipContext.Provider>
  );
};

// Root tooltip component
export const Tooltip: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <div className="relative inline-block">{children}</div>;
};

// Trigger element that shows/hides tooltip
export const TooltipTrigger: React.FC<{
  children: React.ReactNode;
  asChild?: boolean;
}> = ({ children, asChild = false }) => {
  const { setIsOpen } = useContext(TooltipContext);

  const handleMouseEnter = () => setIsOpen(true);
  const handleMouseLeave = () => setIsOpen(false);

  if (asChild) {
    return React.cloneElement(children as React.ReactElement, {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    });
  }

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {children}
    </div>
  );
};

// Actual tooltip content
export const TooltipContent: React.FC<{
  children: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
}> = ({ children, side = "top" }) => {
  const { isOpen } = useContext(TooltipContext);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isOpen && tooltipRef.current) {
      const parent = tooltipRef.current.parentElement;
      if (!parent) return;

      const parentRect = parent.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      let top = 0;
      let left = 0;

      switch (side) {
        case "top":
          top = -tooltipRect.height - 8;
          left = (parentRect.width - tooltipRect.width) / 2;
          break;
        case "bottom":
          top = parentRect.height + 8;
          left = (parentRect.width - tooltipRect.width) / 2;
          break;
        case "left":
          top = (parentRect.height - tooltipRect.height) / 2;
          left = -tooltipRect.width - 8;
          break;
        case "right":
          top = (parentRect.height - tooltipRect.height) / 2;
          left = parentRect.width + 8;
          break;
      }

      setPosition({ top, left });
    }
  }, [isOpen, side]);

  if (!isOpen) return null;

  return (
    <div
      ref={tooltipRef}
      className={`
        absolute z-50 px-2 py-1
        text-sm text-white bg-gray-800
        rounded shadow-lg transition-opacity
        ${isOpen ? "opacity-100" : "opacity-0"}
      `}
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      {children}
      <div
        className={`
          absolute w-2 h-2 bg-gray-800
          transform rotate-45
          ${side === "top" ? "bottom-[-4px] left-1/2 -translate-x-1/2" : ""}
          ${side === "bottom" ? "top-[-4px] left-1/2 -translate-x-1/2" : ""}
          ${side === "left" ? "right-[-4px] top-1/2 -translate-y-1/2" : ""}
          ${side === "right" ? "left-[-4px] top-1/2 -translate-y-1/2" : ""}
        `}
      />
    </div>
  );
};
